import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { buildTableOfContents, buildOutlineMarkdown } from "@/utils/exportMarkdown";
import type { ExportSection, ExportDocument } from "@/utils/exportMarkdown";
import { projectsApi, LUMANITY_DOC_STYLE } from "@/services/projectsApi";
import type { Project } from "@/types/models";
import { useUsers } from "@/hooks/useUsers";

export function useExportOutline() {
  const auth = useAuth();
  const { data: allUsers = [] } = useUsers();
  const [isExporting, setIsExporting] = useState(false);

  const exportOutline = async ({
    templateName,
    orderedSections,
    projectDocuments,
    project,
  }: {
    templateName: string;
    orderedSections: ExportSection[];
    projectDocuments: ExportDocument[];
    project: Pick<Project, "id" | "name" | "code" | "clientIds" | "clientNames" | "therapyArea" | "indication" | "condition" | "objective" | "projectType" | "memberIds"> | null | undefined;
  }) => {
    setIsExporting(true);
    try {
      const preparedFor = (project?.clientNames ?? []).join(", ");
      const memberNames = (project?.memberIds ?? [])
        .map((id) => allUsers.find((u) => u.id === id))
        .filter(Boolean)
        .map((u) => `${u!.first_name} ${u!.last_name}`.trim() || u!.email);
      const preparedBy = memberNames.length > 0
        ? memberNames.join(", ")
        : auth.user?.firstName && auth.user?.lastName
          ? `${auth.user.firstName} ${auth.user.lastName}`
          : (auth.user?.email ?? "");
      const reportDate = new Date().toLocaleDateString("en-GB", {
        day: "numeric", month: "long", year: "numeric",
      });

      let imageBase64: string | undefined;
      try {
        const resp = await fetch("/lumanity_header.png");
        if (resp.ok) {
          const buf = await resp.arrayBuffer();
          const arr = new Uint8Array(buf);
          let binary = "";
          for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
          imageBase64 = btoa(binary);
        }
      } catch { /* skip image if unavailable */ }

      const coverPageMarkdown = [
        `# ${templateName}`, "",
        `## Report date:`, "", reportDate, "",
        `## Prepared for:`, "", preparedFor, "",
        `## Prepared by:`, "", preparedBy,
      ].join("\n");

      const toc = buildTableOfContents(orderedSections);
      const bodyMd = buildOutlineMarkdown(templateName, orderedSections, projectDocuments);
      const filename = `${templateName.replace(/\s+/g, "_")}_outline.docx`;

      const customProperties: Record<string, string> = {};
      if (project?.id) customProperties.pico_project_id = project.id;
      if (project?.name) customProperties.pico_project_name = project.name;
      if (project?.code) customProperties.pico_project_code = project.code;
      if (project?.clientNames?.length) customProperties.pico_client_name = project.clientNames.join(", ");
      if (project?.clientIds?.length) customProperties.pico_client_id = project.clientIds.join(", ");
      if (project?.therapyArea) customProperties.pico_therapy_area = project.therapyArea;
      if (project?.indication) customProperties.pico_indication = project.indication;
      if (project?.condition) customProperties.pico_condition = project.condition;
      if (project?.objective) customProperties.pico_objective = project.objective;
      if (project?.projectType) customProperties.pico_project_type = project.projectType;

      await projectsApi.generateMarkdownWord(toc + bodyMd, filename, LUMANITY_DOC_STYLE, {
        is_coverpage_required: true,
        coverpage_content: coverPageMarkdown,
        ...(imageBase64 ? { cover_image_base64: imageBase64 } : {}),
      }, customProperties);
    } finally {
      setIsExporting(false);
    }
  };

  return { exportOutline, isExporting };
}
